export const useNotificationPrompt = () => {
  const { loggedIn } = useUserSession();
  const toast = useToast();

  const shouldShowPrompt = () => {
    if (!loggedIn.value) return false;

    if (typeof window === 'undefined' || !('Notification' in window) || !('serviceWorker' in navigator)) {
      return false;
    }

    const promptShown = sessionStorage.getItem('notification_prompt_shown');
    if (promptShown === 'true') {
      return false;
    }

    const permission = Notification.permission;
    return permission === 'default';
  };

  const showPrompt = async () => {
    if (!shouldShowPrompt()) return;

    sessionStorage.setItem('notification_prompt_shown', 'true');

    await new Promise((resolve) => setTimeout(resolve, 2000));

    toast.add({
      id: 'notification-prompt',
      title: '🔔 Ative as Notificações',
      description: 'Receba alertas importantes sobre atualizações e eventos do sistema.',
      actions: [
        {
          label: 'Ativar',
          onClick: async () => {
            await requestNotificationPermission();
          }
        },
        {
          label: 'Agora não',
          onClick: () => {
            toast.remove('notification-prompt');
          }
        }
      ]
    });
  };

  const requestNotificationPermission = async () => {
    try {
      const pushNotifications = usePushNotifications();
      const { requestPermission, subscribe, initialize } = pushNotifications;

      await initialize();

      const permission = await requestPermission();

      if (permission === 'granted') {
        const keysResponse = await $fetch<{
          success: boolean;
          publicKey: string;
        }>('/api/push/keys');
        const vapidPublicKey = keysResponse?.publicKey;

        if (vapidPublicKey) {
          const newSubscription = await subscribe(vapidPublicKey);

          if (newSubscription) {
            const deviceName = detectDeviceName();

            await $fetch('/api/push/subscribe', {
              method: 'POST',
              body: {
                endpoint: newSubscription.endpoint,
                keys: {
                  p256dh: btoa(String.fromCharCode(...new Uint8Array(newSubscription.getKey('p256dh')!))),
                  auth: btoa(String.fromCharCode(...new Uint8Array(newSubscription.getKey('auth')!)))
                },
                deviceName
              }
            });

            toast.remove('notification-prompt');
            toast.add({
              title: '✅ Notificações Ativadas',
              description: 'Você receberá notificações importantes em todos os seus dispositivos.',
              class: 'success',
              duration: 3000
            });
          }
        }
      } else if (permission === 'denied') {
        toast.remove('notification-prompt');
        toast.add({
          title: 'Notificações Bloqueadas',
          description: 'Para ativar, acesse as configurações do navegador e permita notificações para este site.',
          color: 'warning',
          duration: 5000
        });
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      toast.remove('notification-prompt');
      toast.add({
        title: 'Erro ao Ativar Notificações',
        description: error instanceof Error ? error.message : 'Erro desconhecido',
        class: 'error',
        duration: 5000
      });
    }
  };

  const detectDeviceName = (): string => {
    if (typeof navigator === 'undefined') return 'Dispositivo Desconhecido';

    const ua = navigator.userAgent;

    if (/mobile/i.test(ua)) {
      if (/android/i.test(ua)) return 'Android';
      if (/iPhone|iPad|iPod/i.test(ua)) return 'iOS';
      return 'Mobile';
    }

    if (/Windows/i.test(ua)) return 'Windows';
    if (/Mac/i.test(ua)) return 'Mac';
    if (/Linux/i.test(ua)) return 'Linux';

    if (/Chrome/i.test(ua)) return 'Chrome';
    if (/Firefox/i.test(ua)) return 'Firefox';
    if (/Safari/i.test(ua)) return 'Safari';
    if (/Edge/i.test(ua)) return 'Edge';

    return 'Navegador';
  };

  return {
    shouldShowPrompt,
    showPrompt,
    requestNotificationPermission
  };
};
